import { useEffect, useState } from 'react';
import type { LoincHit, LoincSearchResult, LoincStatus } from '@dru/types';
import { EntityCombobox } from '@dru/ui';
import { api } from '../../../lib/api';

/**
 * Maps a catalogue test to its LOINC term.
 *
 * The internal code is untouched by anything here — 'PAT-HB' is what
 * protocols, imports and every past report refer to. This records the same
 * test's identity to everybody else: an analyzer's HL7 OBX-3, a partner lab's
 * capability list and a FHIR Observation all speak LOINC and none of them has
 * heard of 'PAT-HB'.
 *
 * Built on EntityCombobox rather than a `<select>` for the reason that
 * component exists: even the trimmed reference table is two thousand terms,
 * and the full release is 112,405. The server filters and ranks.
 *
 * Deliberately never required. A Standard Threshold Shift is an OSHA
 * calculation rather than a LOINC observation, and a wrong code is
 * indistinguishable from a right one downstream — so an unmapped test stays
 * unmapped and says so.
 */
export function LoincPicker({ value, onChange, label = 'LOINC term', disabled, className = 'mt-3' }: {
  value: LoincHit | null;
  onChange: (hit: LoincHit | null) => void;
  label?: string;
  disabled?: boolean;
  /** Spacing is the caller's; a form that stacks its own fields passes ''. */
  className?: string;
}) {
  const [status, setStatus] = useState<LoincStatus | null>(null);

  // Asked once. The picker has to be able to say "nobody ran the import"
  // rather than rendering an empty dropdown that looks like a broken search.
  useEffect(() => {
    let live = true;
    api.get<LoincStatus>('/loinc/status')
      .then((s) => { if (live) setStatus(s); })
      .catch(() => undefined);
    return () => { live = false; };
  }, []);

  const notImported = status && !status.imported;

  return (
    <div className={className}>
      <EntityCombobox<LoincHit>
        label={label}
        value={value}
        onChange={onChange}
        disabled={disabled || !!notImported}
        clearable
        minChars={2}
        // Nothing on an empty query: an administrator opening this has a test
        // in mind, and five arbitrary terms is noise rather than a head start.
        initial={0}
        placeholder={notImported ? 'LOINC reference data not loaded' : 'Not mapped'}
        searchPlaceholder="creatinine, haemoglobin, 718-7…"
        emptyMessage="No LOINC term matches. Leave unmapped rather than guessing."
        fetchOptions={async (search) => {
          const res = await api.get<LoincSearchResult>(
            `/loinc/search?q=${encodeURIComponent(search)}&limit=20`,
          );
          // One page. The ranking already puts what somebody meant at the top,
          // and a second page of a LOINC search is a sign the query was wrong
          // rather than that the answer is further down.
          return { items: res.items, nextCursor: null };
        }}
        getKey={(t) => t.loincCode}
        renderValue={(t) => (
          <span className="flex items-baseline gap-2">
            <span className="font-mono text-xs text-neutral-500">{t.loincCode}</span>
            <span className="truncate">{t.longCommonName}</span>
          </span>
        )}
        renderOption={(t) => (
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-xs text-neutral-500">{t.loincCode}</span>
              <span className="truncate text-sm text-neutral-900">{t.longCommonName}</span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-caption text-neutral-500">
              {t.ucumUnit && <span className="font-mono">{t.ucumUnit}</span>}
              {/* Says which of these a laboratory actually orders. Everything
                  else is a long tail the ranking has already pushed down. */}
              {t.isUniversalOrder && (
                <span className="rounded bg-primary-50 px-1 text-primary-700">common order</span>
              )}
              {t.status !== 'ACTIVE' && (
                <span className="rounded bg-warning-100 px-1 text-warning-600">
                  {t.status.toLowerCase()}
                  {t.mapsTo ? ` — replaced by ${t.mapsTo}` : ''}
                </span>
              )}
              {t.class && <span>{t.class}</span>}
            </div>
          </div>
        )}
        hint={
          notImported
            ? status?.hint
            : value
              ? [
                  value.ucumUnit ? `UCUM: ${value.ucumUnit}` : null,
                  value.consumerName ? `Display: “${value.consumerName}”` : null,
                ].filter(Boolean).join(' · ') || undefined
              : 'Optional: search by clinical test name or code, or leave unmapped.'
        }
      />

      {status?.imported && (
        <p className="mt-1 text-caption text-neutral-500">
          LOINC® standard clinical terminology{status.loincVersion ? ` (v${status.loincVersion})` : ''}
        </p>
      )}
      {value?.licenceNotice && (
        <p className="mt-1 text-caption text-warning-600">{value.licenceNotice}</p>
      )}
    </div>
  );
}

/**
 * Resolves a stored mapping back into a picker value.
 *
 * A test row carries the code and the name it was mapped with, not the whole
 * term, so the edit screen would otherwise open showing "Not mapped" for a
 * test that is mapped. This looks the term up and falls back to what the row
 * already knows if the reference table has since been trimmed.
 */
export function useLoincValue(loincCode: string | null, loincLongName: string | null) {
  const [hit, setHit] = useState<LoincHit | null>(null);

  useEffect(() => {
    if (!loincCode) { setHit(null); return; }
    let live = true;
    api.get<LoincSearchResult>(`/loinc/search?q=${encodeURIComponent(loincCode)}&limit=1`)
      .then((res) => {
        if (!live) return;
        setHit(res.items[0] ?? fallback(loincCode, loincLongName));
      })
      .catch(() => { if (live) setHit(fallback(loincCode, loincLongName)); });
    return () => { live = false; };
  }, [loincCode, loincLongName]);

  return [hit, setHit] as const;
}

const fallback = (code: string, name: string | null): LoincHit => ({
  loincCode: code,
  longCommonName: name ?? code,
  shortName: null, consumerName: null, ucumUnit: null,
  class: null, system: null, scaleType: null,
  commonRank: 0, status: 'ACTIVE', isUniversalOrder: false,
  mapsTo: null, licenceNotice: null,
});
